import React, {useEffect, useRef, useState} from 'react';

import * as Sentry from '@sentry/react';
import axios from 'axios';
import type {CancelTokenSource} from 'axios';
import {useIntl} from 'react-intl';

import * as mediaModule from '@edume/bento/media.browser';
import {stripUploadUrlPrefix} from '@edume/bento/videoUtil.browser';
import {buildVideoUrlWithCloudinaryCropParams} from '@edume/bento/videoUtil.browser';
import type {ContentShape} from '@edume/magnificent';
import {useVideoCropper, VideoCropperBody} from '@edume/magnificent';

import {StyledModal} from '../../../containers/StyledModals';
import {useDispatch, useSelector} from '../../../state/hooks';
import {createErrorNotification} from '../../../state/notification/notificationActions';
import {getSelectedGroupId} from '../../../state/team/teamSelectors';
import {
  useAddVideoToLibraryMutation,
  useDeleteFromVideoLibraryMutation,
  useGetVideoLibraryQuery,
} from '../../../state/videoLibrary/videoLibraryApi';
import {handleVideoUpload, readURL} from '../../../utils/fileUpload';
import MediaContainer from '../MediaContainer';
import {MediaLibraryPublicLink} from './MediaLibraryPublicLink';
import type {LastPickedFileState, StepName} from './types';
import getFileSizeErrorStep from './UploadMediaModalSteps/getFileSizeErrorStep';
import getFileSizeWarningStep from './UploadMediaModalSteps/getFileSizeWarningStep';
import {getLibraryStep} from './UploadMediaModalSteps/getLibraryStep';
import getUploadErrorStep from './UploadMediaModalSteps/getUploadErrorStep';
import getUploadPreviewStep from './UploadMediaModalSteps/getUploadPreviewStep';
import getUploadStep from './UploadMediaModalSteps/getUploadStep';
import {MEDIA_LIBRARY_TABS, useMediaLibraryTab} from './useMediaLibraryTab';
import type useUploadModal from './useUploadModal';
import {VideoLibrary} from './VideoLibrary';

import styles from './styles.module.scss';

type UploadVideoModalProps = {
  hintTextKey: string;
  isRemovingEnabled: boolean;
  isCroppingTrimmingEnabled: boolean;
} & Pick<
  ReturnType<typeof useUploadModal>,
  | 'onSetVideo'
  | 'onSetCroppedVideo'
  | 'onSaveChanges'
  | 'onRemove'
  | 'onClose'
  | 'videoUrl'
  | 'croppedVideoUrl'
>;

export const UploadVideoModal = ({
  videoUrl,
  croppedVideoUrl,
  hintTextKey,
  onSetVideo,
  onSetCroppedVideo,
  onSaveChanges,
  onRemove,
  onClose,
  isRemovingEnabled,
  isCroppingTrimmingEnabled,
}: UploadVideoModalProps) => {
  const intl = useIntl();
  const groupId = useSelector(getSelectedGroupId);
  const [step, setStep] = useState<StepName>(() =>
    videoUrl ? 'initial-seeded' : 'library'
  );

  const [libraryTab, setLibraryTab] = useMediaLibraryTab(
    MEDIA_LIBRARY_TABS.GROUP_VIDEOS
  );

  const [lastPickedFile, setLastPickedFile] = useState<
    Exclude<LastPickedFileState, {type: 'image'}>
  >({
    type: null,
    content: null,
  });

  const [isVideoDownloading, setIsVideoDownloading] = useState(false);

  const axiosCancelSource = useRef<CancelTokenSource | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const selectedGroupId = useSelector(getSelectedGroupId);
  const {data} = useGetVideoLibraryQuery(selectedGroupId);
  const externalVideos = data?.externalVideos ?? [];
  const [addVideoMutation] = useAddVideoToLibraryMutation();
  const [deleteVideoMutation] = useDeleteFromVideoLibraryMutation();

  const dispatch = useDispatch();

  const {getCropResult, ...videoCropperProps} = useVideoCropper({
    sourceFile:
      lastPickedFile.content instanceof File ? lastPickedFile.content : null,
  });

  useEffect(() => {
    axiosCancelSource.current = axios.CancelToken.source();
    return () => {
      axiosCancelSource.current?.cancel(
        mediaModule.CANCELLED_COMPONENT_UNMOUNTED
      );
    };
  }, []);

  const startUpload = async (files: File[], {ignoreWarning = false} = {}) => {
    if (files.length) {
      setStep('uploading');
      const file = files[0];
      const fileName = file.name;
      const url = await readURL(file);
      setLastPickedFile({type: 'file', content: file, url});
      if (mediaModule.shouldErrorFileTooBig(file, 'video')) {
        setStep('file-size-error');
      } else if (
        mediaModule.shouldWarnFileTooBig(file, 'video') &&
        !ignoreWarning
      ) {
        setStep('file-size-warning');
      } else {
        handleVideoUpload(file, axiosCancelSource.current)
          .then((uploadedVideo) => {
            setStep(isCroppingTrimmingEnabled ? 'crop' : 'success');

            onSetVideo(uploadedVideo.url, {fileName});
          })
          .catch((err) => {
            if (err.message !== mediaModule.CANCELLED_COMPONENT_UNMOUNTED) {
              setStep('upload-error');
              Sentry.captureException(
                new Error('error uploading video to cloudinary'),
                {
                  extra: {
                    error: err,
                  },
                }
              );
              throw err;
            }
          });
      }
    } else {
      dispatch(createErrorNotification('UploadModal.error.fileType'));
    }
  };

  const chooseVideo = async (video: {
    id: number;
    url: string;
    filename: string;
  }) => {
    if (isCroppingTrimmingEnabled) {
      setStep('crop');
      await downloadRemoteVideoForCropping({
        videoId: video.id,
        url: video.url,
        filename: video.filename,
      });
    } else {
      const strippedVideoUrl = stripUploadUrlPrefix(video.url);

      setLastPickedFile({
        type: 'video',
        content: {name: video.filename},
        url: video.url,
        videoId: video.id,
      });
      onSetVideo(strippedVideoUrl, {fileName: video.filename});

      setStep('success');
    }
  };

  const downloadRemoteVideoForCropping = async ({
    videoId,
    url,
    filename,
  }: {
    videoId: number;
    url: string;
    filename: string;
  }) => {
    try {
      setIsVideoDownloading(true);

      const response = await fetch(url);
      const blob = await response.blob();
      const file = new File([blob], filename, {
        type: blob.type,
      });

      const strippedVideoUrl = stripUploadUrlPrefix(url);
      setLastPickedFile({
        type: 'video',
        content: file,
        url,
        videoId,
      });
      onSetVideo(strippedVideoUrl, {fileName: filename});
    } catch (error) {
      setStep('upload-error');
      Sentry.captureException(
        new Error('Failed to download video for cropping'),
        {
          extra: {error, video: lastPickedFile},
        }
      );
    } finally {
      setIsVideoDownloading(false);
    }
  };

  const confirmCrop = () => {
    if (!videoUrl) {
      setStep('upload-error');
      return;
    }

    const result = getCropResult();

    if (!result || !result.croppedAreaPixels) {
      setStep('upload-error');
      return;
    }

    const {videoStart, videoEnd, croppedAreaPixels} = result ?? {};
    const {width, height, x, y} = croppedAreaPixels ?? {};

    const videoWithCropParams = buildVideoUrlWithCloudinaryCropParams({
      videoUrl,
      x,
      y,
      width,
      height,
      videoStart,
      videoEnd,
      duration: videoCropperProps.videoDuration,
      zoom: videoCropperProps.zoom,
      minZoom: videoCropperProps.minZoom,
    });

    onSetCroppedVideo(videoWithCropParams, {
      fileName: lastPickedFile.content?.name,
    });

    setStep('success');
  };

  const deleteVideo = (id: number) => {
    const isExternalVideo = externalVideos.some((video) => video.id === id);

    deleteVideoMutation({groupId, videoId: id});

    if (libraryTab === MEDIA_LIBRARY_TABS.EXTERNAL_VIDEOS && isExternalVideo) {
      // If this was the last external video, switch to group library
      if (externalVideos.length === 1) {
        setLibraryTab(MEDIA_LIBRARY_TABS.GROUP_VIDEOS);
      }
    }
  };

  const retryUpload = () => {
    if (lastPickedFile.type !== 'file') {
      dispatch(createErrorNotification('Error.generic'));
      throw new Error('Called with invalid state');
    }

    startUpload([lastPickedFile.content], {ignoreWarning: true});
  };

  const goToStart = () => {
    setStep(videoUrl ? 'initial-seeded' : 'library');
  };

  const goToLibrary = () => {
    setStep('library');
  };

  const onConfirm = async () => {
    if (lastPickedFile.type === 'file' && lastPickedFile.content && videoUrl) {
      try {
        await addVideoMutation({
          file: lastPickedFile.content,
          videoUrl,
          groupId: selectedGroupId,
        }).unwrap();
      } catch (error) {
        Sentry.captureException(new Error('error uploading video to library'), {
          extra: {
            error,
          },
        });
      }
    }
    onSaveChanges();
  };

  const getUploadPreviewStepCurried = ({
    hasNewUpload,
  }: {
    hasNewUpload: boolean;
  }) =>
    getUploadPreviewStep({
      type: 'video',
      intl,
      onClose: hasNewUpload ? onConfirm : onClose,
      body: (
        <MediaContainer type='video' mediaUrl={croppedVideoUrl ?? videoUrl} />
      ),
      isRemovingEnabled,
      onRemove,
      startUpload,
      goToLibrary,
      hasNewUpload,
    });

  const steps = {
    initial: () =>
      getUploadStep({
        type: 'video',
        intl,
        onClose,
        hintTextKey,
        isUploading: false,
        onFilePick: startUpload,
      }),
    library: () =>
      getLibraryStep({
        libraryTab,
        setLibraryTab,
        videoLibrary: (
          <VideoLibrary
            startUpload={startUpload}
            chooseVideo={chooseVideo}
            deleteVideo={deleteVideo}
            setIsDeleteModalOpen={setIsDeleteModalOpen}
            isGroupLibrary={libraryTab === MEDIA_LIBRARY_TABS.GROUP_VIDEOS}
          />
        ),
        uploadLink: (
          <div className={styles['library-container']}>
            <MediaLibraryPublicLink />
          </div>
        ),
        onClose,
        intl,
      }),
    'initial-seeded': () => getUploadPreviewStepCurried({hasNewUpload: false}),
    'file-size-warning': () =>
      getFileSizeWarningStep({
        type: 'video',
        intl,
        retryUpload,
        goBack: goToStart,
      }),
    'file-size-error': () =>
      getFileSizeErrorStep({type: 'video', intl, goBack: goToStart}),
    uploading: () =>
      getUploadStep({
        type: 'video',
        intl,
        onClose,
        hintTextKey,
        isUploading: true,
      }),
    'upload-error': () =>
      getUploadErrorStep({intl, onCancel: onClose, retryUpload}),
    crop: () => ({
      title: intl.formatMessage({
        id: 'Authoring.uploadMedia.video.crop',
        defaultMessage: 'Trim and crop',
      }),
      body: (
        <VideoCropperBody
          videoCropperProps={videoCropperProps}
          isLoading={isVideoDownloading}
        />
      ),
      buttons: [
        {
          text: intl.formatMessage({
            id: 'Button.cancel',
            defaultMessage: 'Cancel',
          }),
          onClick: onClose,
          type: 'secondary',
        },
        {
          text: intl.formatMessage({
            id: 'Button.confirm',
            defaultMessage: 'Confirm',
          }),
          onClick: confirmCrop,
          type: 'primary',
          dataAutomation: 'confirm-trim-crop-button',
        },
      ],
    }),
    success: () => getUploadPreviewStepCurried({hasNewUpload: true}),
  } satisfies Record<StepName, () => ContentShape>;

  return (
    <StyledModal
      onClose={onClose}
      content={steps[step]()}
      underlayClickExits={!isDeleteModalOpen}
      compact
    />
  );
};
